// Global Instructions Rule Applied!
import { ContactRecordFields } from '../../../state_manager/data_models/contact_record.ts'
import type { FormInstance } from 'antd'

export interface BusinessRuleResult {
  formData: Record<string, any>
  formUpdates?: Record<string, any>
  helperFieldUpdates?: Record<string, any>
}

/**
 * Handle city change - auto-populate country when city is selected
 */
function handleCityChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>,
  countriesData: any[] = []
): BusinessRuleResult | null {
  if (
    field !== ContactRecordFields.contacts__address_city_label &&
    field !== ContactRecordFields.contacts__address_city_code
  )
    return null

  const newData = { ...currentFormData, [field]: value }
  const formUpdates: Record<string, any> = {}

  const cityCode = value?.value || value?.cityCode
  const cityLabel = value?.name || value?.label || ''
  const countryCode = value?.data?.countryCode || value?.countryCode
  const state = value?.data?.state ?? null

  if (cityCode !== undefined && cityCode !== null) {
    const numericCityCode = typeof cityCode === 'string' ? parseInt(cityCode, 10) : cityCode
    if (!isNaN(numericCityCode)) {
      newData[ContactRecordFields.contacts__address_city_code] = numericCityCode
      formUpdates[ContactRecordFields.contacts__address_city_code] = numericCityCode
    }
  }

  if (cityLabel) {
    newData[ContactRecordFields.contacts__address_city_label] = cityLabel
    formUpdates[ContactRecordFields.contacts__address_city_label] = cityLabel
  }

  newData[ContactRecordFields.contacts__address_stateprovince] = state || null
  formUpdates[ContactRecordFields.contacts__address_stateprovince] = state || null

  if (countryCode) {
    newData[ContactRecordFields.contacts__address_country_code] = countryCode
    formUpdates[ContactRecordFields.contacts__address_country_code] = countryCode

    // Populate country label if countriesData is available
    if (countriesData.length > 0) {
      const countryLabel = countriesData.find((country: any) => country.code === countryCode)?.name
      if (countryLabel) {
        newData[ContactRecordFields.contacts__address_country_label] = countryLabel
        formUpdates[ContactRecordFields.contacts__address_country_label] = countryLabel
      }
    }
  }

  return {
    formData: newData,
    formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined
  }
}

/**
 * Handle country change - clear city when country changes
 */
function handleCountryChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>
): BusinessRuleResult | null {
  if (
    field !== ContactRecordFields.contacts__address_country_code &&
    field !== ContactRecordFields.contacts__address_country_label
  )
    return null

  const newData = { ...currentFormData, [field]: value }
  const formUpdates: Record<string, any> = {}

  // Extract country code and label from country object
  const countryCode = value?.value || value
  const countryLabel = value?.label || value

  // Clear city and state when country changes
  newData[ContactRecordFields.contacts__address_city_code] = null
  newData[ContactRecordFields.contacts__address_city_label] = null
  newData[ContactRecordFields.contacts__address_stateprovince] = null
  formUpdates[ContactRecordFields.contacts__address_city_code] = null
  formUpdates[ContactRecordFields.contacts__address_city_label] = null
  formUpdates[ContactRecordFields.contacts__address_stateprovince] = null

  // Set or clear country code and label (must clear both so City search filter resets)
  if (countryCode) {
    newData[ContactRecordFields.contacts__address_country_code] = countryCode
    formUpdates[ContactRecordFields.contacts__address_country_code] = countryCode
  } else {
    newData[ContactRecordFields.contacts__address_country_code] = null
    formUpdates[ContactRecordFields.contacts__address_country_code] = null
  }

  if (countryLabel && countryLabel !== countryCode) {
    newData[ContactRecordFields.contacts__address_country_label] = countryLabel
    formUpdates[ContactRecordFields.contacts__address_country_label] = countryLabel
  } else if (!countryCode) {
    newData[ContactRecordFields.contacts__address_country_label] = null
    formUpdates[ContactRecordFields.contacts__address_country_label] = null
  }

  return {
    formData: newData,
    formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined
  }
}

/**
 * Main handler for field changes - applies all business rules
 */
export function handleFieldChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>,
  form: FormInstance,
  countriesData: any[] = []
): BusinessRuleResult {
  let newData = { ...currentFormData, [field]: value }

  const cityResult = handleCityChange(field, value, newData, countriesData)
  if (cityResult) {
    newData = cityResult.formData
    if (cityResult.formUpdates) {
      form.setFieldsValue(cityResult.formUpdates)
    }
    return cityResult
  }

  const countryResult = handleCountryChange(field, value, newData)
  if (countryResult) {
    newData = countryResult.formData
    if (countryResult.formUpdates) {
      form.setFieldsValue(countryResult.formUpdates)
    }
    return countryResult
  }

  // Default: just update the field
  return {
    formData: newData
  }
}
